import React, { useState, useEffect, useCallback } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ToastContainer, toast } from 'react-toastify';
import { ConfirmacionEliminar } from '../elementos/eventos/avisos/VentanaEmergente';
import { fetchPeriodosVerano, crearPeriodoVerano, actualizarPeriodoVerano, eliminarPeriodoVerano } from '../../peticiones_API/PeticionesPeriodosVerano';
import './GestorPeriodosVerano.css';

function GestorPeriodosVerano() {
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    nombre: '',
    anio: new Date().getFullYear(),
    fecha_inicio: '',
    fecha_fin: '',
    dias_obligatorios: 10
  });

  const confirmDialog = ConfirmacionEliminar();
  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const cargarPeriodos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPeriodosVerano({ anio: filtroAnio });
      setPeriodos(data);
    } catch (error) {
      console.error('Error al cargar periodos:', error);
      toast.error('Error al cargar los periodos');
    } finally {
      setLoading(false);
    }
  }, [filtroAnio]);

  useEffect(() => {
    cargarPeriodos();
  }, [cargarPeriodos]);

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      if (editando) {
        await actualizarPeriodoVerano(editando.Id, formData);
        toast.success('Periodo actualizado');
      } else {
        await crearPeriodoVerano(formData);
        toast.success('Periodo creado');
      }
      setShowModal(false);
      resetForm();
      cargarPeriodos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEliminar = async (id) => {
    const confirmado = await confirmDialog.showAlert('eliminar', 'periodo de verano');
    if (confirmado) {
      try {
        await eliminarPeriodoVerano(id);
        toast.success('Periodo eliminado');
        cargarPeriodos();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar');
      }
    }
  };

  const handleEditar = (periodo) => {
    setEditando(periodo);
    setFormData({
      nombre: periodo.nombre || '',
      anio: periodo.anio,
      fecha_inicio: periodo.fecha_inicio.split('T')[0],
      fecha_fin: periodo.fecha_fin.split('T')[0],
      dias_obligatorios: periodo.dias_obligatorios
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      anio: filtroAnio,
      fecha_inicio: '',
      fecha_fin: '',
      dias_obligatorios: 10
    });
    setEditando(null);
  };

  const esActual = (periodo) => periodo.anio === new Date().getFullYear();
  const esFuturo = (periodo) => periodo.anio > new Date().getFullYear();

  if (loading) {
    return (
      <div className="gestor-periodos-loading">
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="gestor-periodos-verano">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#336385' }}>
          Periodos de Verano
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Año</InputLabel>
            <Select
              value={filtroAnio}
              label="Año"
              onChange={(e) => setFiltroAnio(e.target.value)}
            >
              {anios.map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setShowModal(true); }}
            sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
          >
            Nuevo Periodo
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {periodos.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No hay periodos de verano configurados
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Año</strong></TableCell>
                    <TableCell><strong>Fecha Inicio</strong></TableCell>
                    <TableCell><strong>Fecha Fin</strong></TableCell>
                    <TableCell><strong>Días Obligatorios</strong></TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodos.map((periodo) => (
                    <TableRow key={periodo.Id} hover>
                      <TableCell>{periodo.nombre || '-'}</TableCell>
                      <TableCell>{periodo.anio}</TableCell>
                      <TableCell>
                        {new Date(periodo.fecha_inicio).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell>
                        {new Date(periodo.fecha_fin).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell>{periodo.dias_obligatorios} días</TableCell>
                      <TableCell>
                        {esActual(periodo) ? (
                          <Chip label="Actual" color="success" size="small" />
                        ) : esFuturo(periodo) ? (
                          <Chip label="Futuro" color="info" size="small" />
                        ) : (
                          <Chip label="Pasado" variant="outlined" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => handleEditar(periodo)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleEliminar(periodo.Id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          {editando ? 'Editar Periodo' : 'Nuevo Periodo de Verano'}
        </DialogTitle>
        <form onSubmit={handleGuardar}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Verano 2026"
            />
            <TextField
              fullWidth
              margin="normal"
              label="Año"
              type="number"
              value={formData.anio}
              onChange={(e) => setFormData({ ...formData, anio: parseInt(e.target.value) })}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Fecha Inicio"
              type="date"
              value={formData.fecha_inicio}
              onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Fecha Fin"
              type="date"
              value={formData.fecha_fin}
              onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Días Obligatorios"
              type="number"
              value={formData.dias_obligatorios}
              onChange={(e) => setFormData({ ...formData, dias_obligatorios: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 30 }}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)} sx={{ color: '#333' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
            >
              {editando ? 'Guardar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default GestorPeriodosVerano;
